import Plus from "assets/img/svg/plus";
import CopyToClipboard from "components/shared/CopyToClipboard";
import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import PenEditButton from "components/shared/PenEditButton";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import TitleSeparation from "components/shared/TitleSeparation";
import { BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITitleFormatType } from "store/titleFormatTypeStore";
import { deleteTitleFormatType, fetchTitleFormatTypes } from "store/titleFormatTypeStore";

import FormatTypeForm from "./FormatTypeForm";
import styles from "./style.module.scss";

const FORMAT_TYPE_STEPS = [
    { titleKey: "FormatTypesStepCreateTitle", descriptionKey: "FormatTypesStepCreateDescription" },
    { titleKey: "FormatTypesStepCardTitle", descriptionKey: "FormatTypesStepCardDescription" },
    { titleKey: "FormatTypesStepContractTitle", descriptionKey: "FormatTypesStepContractDescription" },
] as const;

const FormatTypesSettings = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { items, status } = useAppSelector((state) => state.titleFormatTypes);
    const [modalType, setModalType] = useState<"create" | "edit" | "delete" | "">("");
    const [chosenType, setChosenType] = useState<ITitleFormatType | undefined>(undefined);

    useEffect(() => {
        if (status === "idle") {
            dispatch(fetchTitleFormatTypes());
        }
    }, [dispatch, status]);

    const closeModal = () => {
        setModalType("");
        setChosenType(undefined);
    };

    const handleDelete = async () => {
        if (!chosenType) return;
        const response = await dispatch(deleteTitleFormatType({ id: chosenType.id }));
        if (response.meta.requestStatus === "fulfilled") {
            closeModal();
        }
    };

    return (
        <div className={styles["format-types-settings"]}>
            <div className={styles.header}>
                <h1>{t("Settings.FormatTypes")}</h1>
                <StyledButton
                    buttonType="primary"
                    onClick={() => {
                        setChosenType(undefined);
                        setModalType("create");
                    }}
                >
                    <Plus />
                    {t("Settings.AddFormatType")}
                </StyledButton>
            </div>

            <div className={styles.guide}>
                <TitleSeparation
                    title={t("Settings.FormatTypesHowItWorks")}
                    description={t("Settings.FormatTypesHowItWorksDescription")}
                    style={{ marginTop: "1.5rem" }}
                />
                <ol className={styles.steps}>
                    {FORMAT_TYPE_STEPS.map(({ titleKey, descriptionKey }, index) => (
                        <li key={titleKey} className={styles.step}>
                            <span className={styles["step-number"]}>{index + 1}</span>
                            <div className={styles["step-content"]}>
                                <BodyHighlight>{t(`Settings.${titleKey}`)}</BodyHighlight>
                                <SmallBody color="grey">{t(`Settings.${descriptionKey}`)}</SmallBody>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className={styles.cards}>
                    <div className={styles.card}>
                        <BodyHighlight>{t("Settings.FormatTypesDefaultFormulaTitle")}</BodyHighlight>
                        <SmallBody color="grey">{t("Settings.FormatTypesDefaultFormulaDescription")}</SmallBody>
                    </div>
                    <div className={styles.card}>
                        <BodyHighlight>{t("Settings.FormatTypesOverrideTitle")}</BodyHighlight>
                        <SmallBody color="grey">{t("Settings.FormatTypesOverrideDescription")}</SmallBody>
                    </div>
                </div>
            </div>

            <div className={styles["list-section"]}>
                <TitleSeparation
                    title={t("Settings.FormatTypesListSection")}
                    description={t("Settings.FormatTypesListSectionDescription")}
                    style={{ marginTop: "2.5rem" }}
                />

                {status === "pending" && (
                    <div className={styles["spinner-container"]}>
                        <Spinner />
                    </div>
                )}

                {status !== "pending" && items.length === 0 && (
                    <div className={styles["empty-state"]}>
                        <p>{t("Settings.FormatTypesEmpty")}</p>
                        <span>{t("Settings.FormatTypesEmptyHint")}</span>
                    </div>
                )}

                {status !== "pending" && items.length > 0 && (
                    <div className={styles["format-types-list"]}>
                        {items.map((formatType) => (
                            <div key={formatType.id} className={styles["format-type-row"]}>
                                <div className={styles["format-type-identity"]}>
                                    <span className={styles["format-type-name"]}>{formatType.name}</span>
                                    <CopyToClipboard className={styles["format-type-id"]} color="var(--foreground)">
                                        {`#${formatType.id}`}
                                    </CopyToClipboard>
                                </div>
                                <div className={styles["format-type-actions"]}>
                                    <PenEditButton
                                        tooltipText={t("Settings.FormatTypeEdit")}
                                        onClick={() => {
                                            setChosenType(formatType);
                                            setModalType("edit");
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ModalBase
                open={modalType === "create" || modalType === "edit"}
                onClose={closeModal}
                modalTitle={modalType === "edit" ? t("Settings.EditFormatType") : t("Settings.NewFormatType")}
                modalSubtitle={t("Settings.FormatTypeFormHint")}
            >
                <FormatTypeForm
                    key={chosenType?.id ?? "new"}
                    formatType={chosenType}
                    onClose={closeModal}
                    onRequestDelete={() => setModalType("delete")}
                />
            </ModalBase>

            <DeleteModal
                open={modalType === "delete"}
                onClose={closeModal}
                onDelete={handleDelete}
                elementTitle={chosenType?.name ?? ""}
                needsDoubleQuotes
                description={t("Settings.DeleteFormatTypeDescription")}
            />
        </div>
    );
};

export default FormatTypesSettings;
